import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Star, Monitor, Wifi, WifiOff, Phone, PhoneIncoming, PhoneOutgoing, MessageSquare, Mail, Users, FileText, Trash2, ExternalLink, Ticket, Link2, Copy, Trash } from 'lucide-react';
import { deviceTypeInfo, connectionTypeLabel } from '../utils/deviceConfig';

const JOURNAL_TYPES = [
  { value: 'phone_in',      label: 'Anruf erhalten',         icon: PhoneIncoming  },
  { value: 'phone_out',     label: 'Anruf getaetigt',        icon: PhoneOutgoing  },
  { value: 'messenger_in',  label: 'Messenger erhalten',     icon: MessageSquare  },
  { value: 'messenger_out', label: 'Messenger gesendet',     icon: MessageSquare  },
  { value: 'email_in',      label: 'E-Mail erhalten',        icon: Mail           },
  { value: 'email_out',     label: 'E-Mail gesendet',        icon: Mail           },
  { value: 'personal',      label: 'Persoenlicher Kontakt',  icon: Users          },
  { value: 'note',          label: 'Interne Notiz',          icon: FileText       },
];

function typeInfo(type) {
  return JOURNAL_TYPES.find(t => t.value === type) || { label: type, icon: FileText };
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function groupByMonth(entries) {
  const groups = {};
  entries.forEach(e => {
    const d = new Date(e.created_at);
    const key = d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    if (!groups[key]) groups[key] = [];
    groups[key].push(e);
  });
  return groups;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [devices, setDevices] = useState([]);
  const [ninjaOrgs, setNinjaOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [ninjaOrgId, setNinjaOrgId] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', position: '', salutation: '',
    emails: [{ email: '', is_primary: true }], phone: '', notes: '', is_primary: false
  });

  // Magic Link
  const [magicLink, setMagicLink] = useState(null);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  // Journal
  const [journal, setJournal] = useState([]);
  const [showJournalForm, setShowJournalForm] = useState(false);
  const [journalForm, setJournalForm] = useState({ type: 'note', contact_id: '', note: '', followup_at: '', device_id: '' });

  const load = async () => {
    try {
      const [customerRes, contactsRes] = await Promise.all([
        api.get('/customers/' + id),
        api.get('/contacts/customer/' + id),
      ]);
      setCustomer(customerRes.data);
      setNinjaOrgId(customerRes.data.ninja_organization_id || '');
      setContacts(contactsRes.data);

      try {
        const devicesRes = await api.get('/devices?customer_id=' + id);
        setDevices(devicesRes.data);
      } catch (e) {}

      try {
        const orgsRes = await api.get('/ninja/organizations');
        setNinjaOrgs(orgsRes.data);
      } catch (e) {}

    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const loadMagicLink = async () => {
    try {
      const res = await api.get('/magiclinks/customer/' + id);
      setMagicLink(res.data);
    } catch (e) {}
  };

  const handleGenerateLink = async () => {
    setMagicLinkLoading(true);
    try {
      const res = await api.post('/magiclinks/customer/' + id);
      setMagicLink(res.data);
      toast.success('Link generiert');
    } catch (e) {
      toast.error('Fehler beim Generieren');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleDeleteLink = async () => {
    if (!window.confirm('Link wirklich deaktivieren?')) return;
    setMagicLinkLoading(true);
    try {
      await api.delete('/magiclinks/customer/' + id);
      setMagicLink(null);
      toast.success('Link deaktiviert');
    } catch (e) {
      toast.error('Fehler beim Deaktivieren');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const handleCopyLink = () => {
    const url = window.location.origin + '/ticket/' + magicLink.token;
    navigator.clipboard.writeText(url);
    toast.success('Link kopiert');
  };

  const loadJournal = async () => {
    try {
      const res = await api.get('/journal?customer_id=' + id);
      setJournal(res.data);
    } catch (e) {}
  };

  useEffect(() => { load(); loadJournal(); loadMagicLink(); }, [id]);

  const handleSaveNinjaOrg = async () => {
    try {
      await api.put('/customers/' + id, {
        ...customer,
        ninja_organization_id: ninjaOrgId ? parseInt(ninjaOrgId) : null,
      });
      toast.success('NinjaOne Zuordnung gespeichert');
      load();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleSubmit = async () => {
    if (!form.first_name || !form.last_name) {
      toast.error('Vor- und Nachname sind erforderlich');
      return;
    }
    try {
      if (editingContact) {
        await api.put('/contacts/' + editingContact.id, form);
        toast.success('Ansprechpartner gespeichert');
      } else {
        await api.post('/contacts', { ...form, customer_id: parseInt(id) });
        toast.success('Ansprechpartner angelegt');
      }
      setShowForm(false);
      setEditingContact(null);
      setForm({ first_name: '', last_name: '', position: '', salutation: '', emails: [{ email: '', is_primary: true }], phone: '', notes: '', is_primary: false });
      load();
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      first_name: contact.first_name || '',
      salutation: contact.salutation || '',
      last_name: contact.last_name || '',
      position: contact.position || '',
      emails: contact.emails && contact.emails.length > 0 ? contact.emails : [{ email: contact.email || '', is_primary: true }],
      phone: contact.phone || '',
      notes: contact.notes || '',
      is_primary: contact.is_primary || false,
    });
    setShowForm(true);
  };

  const handleDeactivate = async (contactId) => {
    if (!window.confirm('Ansprechpartner wirklich deaktivieren?')) return;
    try {
      await api.delete('/contacts/' + contactId);
      toast.success('Ansprechpartner deaktiviert');
      load();
    } catch (err) {
      toast.error('Fehler beim Deaktivieren');
    }
  };

  const handleJournalSubmit = async () => {
    if (!journalForm.note.trim()) { toast.error('Notiz erforderlich'); return; }
    try {
      await api.post('/journal', {
        customer_id: parseInt(id),
        type: journalForm.type,
        contact_id: journalForm.contact_id ? parseInt(journalForm.contact_id) : null,
        note: journalForm.note,
        followup_at: journalForm.followup_at || null,
        device_id: journalForm.device_id ? parseInt(journalForm.device_id) : null,
      });
      toast.success('Eintrag gespeichert');
      setShowJournalForm(false);
      setJournalForm({ type: 'note', contact_id: '', note: '', followup_at: '', device_id: '' });
      loadJournal();
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleJournalDelete = async (entryId) => {
    if (!window.confirm('Eintrag wirklich loeschen?')) return;
    try {
      await api.delete('/journal/' + entryId);
      toast.success('Eintrag geloescht');
      loadJournal();
    } catch (e) {
      toast.error('Fehler beim Loeschen');
    }
  };

  const handleCreateTicketFromJournal = async (entry) => {
    try {
      const res = await api.post('/tickets', {
        customer_id: entry.customer_id,
        contact_id: entry.contact_id || null,
        title: entry.note.substring(0, 80),
        description: entry.note,
      });
      const ticketId = res.data?.id;
      if (ticketId) {
        await api.patch('/journal/' + entry.id + '/ticket', { ticket_id: ticketId });
        toast.success('Ticket erstellt');
        loadJournal();
      }
    } catch (e) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const nodeClassLabel = (nc) => {
    const map = {
      WINDOWS_WORKSTATION: 'Windows PC', WINDOWS_SERVER: 'Windows Server',
      MAC: 'Mac', LINUX_WORKSTATION: 'Linux', LINUX_SERVER: 'Linux Server',
      VMWARE_VM_HOST: 'VMware Host', ANDROID: 'Android', APPLE_IOS: 'iOS',
    };
    return map[nc] || nc;
  };

  if (loading) return <div className="text-gray-400 text-sm">Laden...</div>;
  if (!customer) return <div className="text-gray-400 text-sm">Kunde nicht gefunden</div>;

  const monthGroups = groupByMonth(journal);

  return (
    <div>
      <button onClick={() => navigate('/customers')}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 mb-4 transition-colors">
        <ArrowLeft size={16} /> Zurueck
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{customer.name}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {customer.customer_number}
            {customer.city ? ' · ' + customer.city : ''}
            {customer.phone ? ' · ' + customer.phone : ''}
          </p>
        </div>
        {!customer.is_active && (
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">Inaktiv</span>
        )}
      </div>

      <div className="sticky top-0 z-10 mb-4 flex items-center gap-1 bg-gray-50/95 backdrop-blur px-1 py-2 border-b border-gray-100">
        <button onClick={() => document.getElementById('contacts')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="text-xs font-medium text-gray-500 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          Ansprechpartner
        </button>
        <button onClick={() => document.getElementById('devices')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="text-xs font-medium text-gray-500 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          Geraete
        </button>
        <Link to={'/tickets?customer_id=' + id + '&customer_name=' + encodeURIComponent(customer.name)}
          className="text-xs font-medium text-gray-500 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          Tickets
        </Link>
        <button onClick={() => document.getElementById('journal')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="text-xs font-medium text-gray-500 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          Journal
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <h3 className="font-semibold text-gray-900 text-sm mb-3">Details</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {customer.street && <div><span className="text-gray-400">Strasse:</span> <span className="text-gray-700">{customer.street}</span></div>}
            {customer.postal_code && <div><span className="text-gray-400">PLZ:</span> <span className="text-gray-700">{customer.postal_code}</span></div>}
            {customer.city && <div><span className="text-gray-400">Stadt:</span> <span className="text-gray-700">{customer.city}</span></div>}
            {customer.email && <div><span className="text-gray-400">E-Mail:</span> <span className="text-gray-700">{customer.email}</span></div>}
            {customer.website && <div><span className="text-gray-400">Webseite:</span> <span className="text-gray-700">{customer.website}</span></div>}
          </div>
          {customer.notes && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-400 mb-1">Notizen</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{customer.notes}</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <h3 className="font-semibold text-gray-900 text-sm mb-3">NinjaOne</h3>
          <div className="flex gap-2">
            <select value={ninjaOrgId} onChange={e => setNinjaOrgId(e.target.value)}
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50">
              <option value="">Keine Zuordnung</option>
              {ninjaOrgs.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <button onClick={handleSaveNinjaOrg}
              className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm">
              Speichern
            </button>
          </div>
          {devices.filter(d => !d.is_third_party).length > 0 && (
            <p className="text-xs text-gray-400 mt-2">{devices.filter(d => !d.is_third_party && !d.offline).length} online · {devices.filter(d => !d.is_third_party && d.offline).length} offline</p>
          )}
        </div>
      </div>


      {/* Magic Link */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Link2 size={15} className="text-gray-400" />
            <h3 className="font-semibold text-gray-900 text-sm">Ticket-Link</h3>
          </div>
          {!magicLink ? (
            <button onClick={handleGenerateLink} disabled={magicLinkLoading}
              className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
              <Plus size={13} /> Link generieren
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={handleCopyLink}
                className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 font-medium px-3 py-1.5 rounded-lg hover:bg-primary-50 transition-colors">
                <Copy size={13} /> Kopieren
              </button>
              <button onClick={handleGenerateLink} disabled={magicLinkLoading}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50">
                Neu generieren
              </button>
              <button onClick={handleDeleteLink} disabled={magicLinkLoading}
                className="text-xs text-red-500 hover:text-red-600 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50">
                <Trash size={13} />
              </button>
            </div>
          )}
        </div>
        {magicLink ? (
          <div>
            <div className="bg-gray-50 rounded-lg px-3 py-2 font-mono text-xs text-gray-600 break-all select-all">
              {window.location.origin}/ticket/{magicLink.token}
            </div>
            {magicLink.last_used_at && (
              <p className="text-xs text-gray-400 mt-2">
                Zuletzt genutzt: {new Date(magicLink.last_used_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400">Kein aktiver Link. Generiere einen Link damit der Kunde direkt Tickets einreichen kann.</p>
        )}
      </div>

      <div id="contacts" className="scroll-mt-20 bg-white rounded-2xl border border-gray-100 shadow-sm mb-4">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 text-sm">Ansprechpartner</h3>
          <button onClick={() => { setShowForm(true); setEditingContact(null); setForm({ first_name: '', last_name: '', position: '', salutation: '', emails: [{ email: '', is_primary: true }], phone: '', notes: '', is_primary: false }); }}
            className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
            <Plus size={14} /> Hinzufuegen
          </button>
        </div>

        {showForm && (
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <h4 className="font-medium text-gray-900 text-sm mb-3">{editingContact ? 'Ansprechpartner bearbeiten' : 'Neuer Ansprechpartner'}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Anrede</label>
                <select value={form.salutation} onChange={e => setForm({...form, salutation: e.target.value})} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">– keine –</option>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Vorname *</label>
                <input type="text" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Nachname *</label>
                <input type="text" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Position</label>
                <input type="text" value={form.position} onChange={e => setForm({...form, position: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">E-Mail-Adressen</label>
                {form.emails.map((e, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <input type="email" value={e.email} onChange={ev => {
                      const updated = [...form.emails];
                      updated[idx] = { ...updated[idx], email: ev.target.value };
                      setForm({ ...form, emails: updated });
                    }} placeholder="E-Mail-Adresse"
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                    {form.emails.length > 1 && (
                      <button type="button" onClick={() => {
                        const updated = form.emails.filter((_, i) => i !== idx);
                        setForm({ ...form, emails: updated });
                      }} className="text-red-400 hover:text-red-600 text-lg font-bold px-2">x</button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, emails: [...form.emails, { email: '', is_primary: false }] })}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium mt-1">
                  + Weitere E-Mail hinzufuegen
                </button>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Telefon</label>
                <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="flex items-center gap-2 mt-4">
                <input type="checkbox" id="is_primary" checked={form.is_primary} onChange={e => setForm({...form, is_primary: e.target.checked})}
                  className="rounded border-gray-300" />
                <label htmlFor="is_primary" className="text-sm text-gray-700">Hauptansprechpartner</label>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Notizen</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 mt-3">
              <button onClick={handleSubmit}
                className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                Speichern
              </button>
              <button onClick={() => { setShowForm(false); setEditingContact(null); }}
                className="border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                Abbrechen
              </button>
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-100">
          {contacts.filter(c => c.is_active).length === 0 && (
            <p className="text-sm text-gray-400 p-4">Keine Ansprechpartner vorhanden</p>
          )}
          {contacts.filter(c => c.is_active).map(c => (
            <div key={c.id} className="p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name}</p>
                  {c.is_primary && <Star size={12} className="text-yellow-500 fill-yellow-500" />}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {c.position ? c.position : ''}
                  {c.emails && c.emails.length > 0 ? (c.position ? ' · ' : '') + c.emails.map(e => e.email).join(', ') : (c.email ? (c.position ? ' · ' : '') + c.email : '')}
                  {c.phone ? ' · ' + c.phone : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleEdit(c)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium px-3 py-1 rounded-lg hover:bg-primary-50 transition-colors">
                  Bearbeiten
                </button>
                <button onClick={() => handleDeactivate(c.id)}
                  className="text-xs text-red-500 hover:text-red-600 font-medium px-3 py-1 rounded-lg hover:bg-red-50 transition-colors">
                  Entfernen
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div id="devices" className="scroll-mt-20 bg-white rounded-2xl border border-gray-100 shadow-sm mb-4">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Monitor size={16} className="text-gray-400" />
            <h3 className="font-semibold text-gray-900 text-sm">Geraete ({devices.length})</h3>
          </div>
          <Link to={'/devices?customer_id=' + id}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 px-3 py-1.5 rounded-lg hover:bg-primary-50 transition-colors">
            Alle Geraete verwalten
          </Link>
        </div>
        <div className="divide-y divide-gray-50">
          {devices.length === 0 && (
            <p className="text-sm text-gray-400 p-4">Keine Geraete hinterlegt</p>
          )}
          {devices.map(d => {
            const typeInfo = deviceTypeInfo(d.device_type);
            const Icon = typeInfo.icon;
            return (
              <Link key={d.id} to={'/devices/' + d.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <Icon size={15} className="text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{d.display_name || d.system_name || d.dns_name || ('Geraet ' + d.id)}</p>
                      {d.is_third_party && (
                        <span className="text-[10px] font-medium bg-fuchsia-50 text-fuchsia-600 px-1.5 py-0.5 rounded-full shrink-0">Fremdgeraet</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {typeInfo.label}
                      {d.connection_type ? ' · ' + connectionTypeLabel(d.connection_type) : ''}
                    </p>
                  </div>
                </div>
                {!d.is_third_party && (
                  <span className={'text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ' + (d.offline ? 'bg-red-50 text-red-500' : 'bg-teal-50 text-teal-600')}>
                    {d.offline ? 'Offline' : 'Online'}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Journal */}
      <div id="journal" className="scroll-mt-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 text-sm">Journal</h3>
          <button onClick={() => setShowJournalForm(!showJournalForm)}
            className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
            <Plus size={14} /> Eintrag
          </button>
        </div>

        {showJournalForm && (
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Typ *</label>
                <select value={journalForm.type} onChange={e => setJournalForm({...journalForm, type: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  {JOURNAL_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ansprechpartner</label>
                <select value={journalForm.contact_id} onChange={e => setJournalForm({...journalForm, contact_id: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Kein Ansprechpartner</option>
                  {contacts.filter(c => c.is_active).map(c => (
                    <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Geraet (optional)</label>
                <select value={journalForm.device_id} onChange={e => setJournalForm({...journalForm, device_id: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Kein Geraet</option>
                  {devices.map(d => (
                    <option key={d.id} value={d.id}>{d.display_name || d.system_name || d.dns_name || ('Geraet ' + d.id)}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Notiz *</label>
                <textarea value={journalForm.note} onChange={e => setJournalForm({...journalForm, note: e.target.value})}
                  rows={3} placeholder="Was ist passiert?"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Wiedervorlage</label>
                <input type="date" value={journalForm.followup_at} onChange={e => setJournalForm({...journalForm, followup_at: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div className="flex gap-3 mt-3">
              <button onClick={handleJournalSubmit}
                className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                Speichern
              </button>
              <button onClick={() => setShowJournalForm(false)}
                className="border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                Abbrechen
              </button>
            </div>
          </div>
        )}

        <div>
          {journal.length === 0 && (
            <p className="text-sm text-gray-400 p-4">Noch keine Eintraege vorhanden</p>
          )}
          {Object.entries(monthGroups).map(([month, entries]) => (
            <div key={month}>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{month}</span>
              </div>
              {entries.map(entry => {
                const info = typeInfo(entry.type);
                const Icon = info.icon;
                return (
                  <div key={entry.id} className="p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="bg-gray-100 rounded-lg p-1.5 shrink-0 mt-0.5">
                          <Icon size={14} className="text-gray-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-xs font-medium text-gray-600">{info.label}</span>
                            {entry.contact_name && (
                              <span className="text-xs text-gray-400">· {entry.contact_name}</span>
                            )}
                            {entry.device_name && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Monitor size={10} />
                                {entry.device_name}
                              </span>
                            )}
                            {entry.followup_at && (
                              <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">
                                Wiedervorlage: {new Date(entry.followup_at).toLocaleDateString('de-DE')}
                              </span>
                            )}
                            {entry.ticket_id ? (
                              <Link to={'/tickets/' + entry.ticket_id}
                                className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center gap-1 hover:bg-blue-100 transition-colors">
                                <ExternalLink size={10} />
                                {entry.ticket_number || 'Ticket'}
                              </Link>
                            ) : (
                              <button onClick={() => handleCreateTicketFromJournal(entry)}
                                className="text-xs bg-gray-100 text-gray-500 hover:bg-primary-50 hover:text-primary-600 px-2 py-0.5 rounded-full flex items-center gap-1 transition-colors">
                                <Ticket size={10} />
                                Ticket erstellen
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.note}</p>
                          <p className="text-xs text-gray-400 mt-1">{formatDate(entry.created_at)}</p>
                        </div>
                      </div>
                      <button onClick={() => handleJournalDelete(entry.id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 transition-all shrink-0 mt-0.5">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
